import { Link, createFileRoute } from '@tanstack/react-router'
import { CheckCircle2, Copy, MoreHorizontal, Pencil, Plus } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import {
  useActivateScenario,
  useDuplicateScenario,
  useRenameScenario,
  useScenarios,
} from '@/api/queries'
import type { ScenarioListItem } from '@/api/types'
import { ConfirmDialog, Modal } from '@/components/ui/dialog'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Skeleton,
} from '@/components/ui/primitives'
import { ScenarioBindingsSection } from '@/features/scenarios/bindings'
import { formatDateTime } from '@/shared/format'
import { apiErrorLabel } from '@/shared/labels'

export const Route = createFileRoute('/_auth/scenarios/')({
  component: ScenariosPage,
})

type DialogKind = 'rename' | 'duplicate' | 'activate'

function ScenariosPage() {
  const scenarios = useScenarios()
  const [dialog, setDialog] = useState<{ kind: DialogKind; scenario: ScenarioListItem } | null>(
    null,
  )
  const [nameValue, setNameValue] = useState('')

  const rename = useRenameScenario()
  const duplicate = useDuplicateScenario()
  const activate = useActivateScenario()

  const openDialog = (kind: DialogKind, scenario: ScenarioListItem) => {
    setDialog({ kind, scenario })
    setNameValue(kind === 'duplicate' ? `${scenario.name} (копия)` : scenario.name)
  }

  const onError = (error: unknown) =>
    toast.error(apiErrorLabel((error as { detail?: unknown }).detail))

  const submitName = () => {
    if (!dialog) return
    const name = nameValue.trim()
    if (!name) {
      toast.error('Укажите название сценария')
      return
    }
    const payload = { id: dialog.scenario.id, name }
    if (dialog.kind === 'rename') {
      rename.mutate(payload, {
        onSuccess: () => {
          toast.success('Сценарий переименован')
          setDialog(null)
        },
        onError,
      })
    } else {
      duplicate.mutate(payload, {
        onSuccess: () => {
          toast.success('Сценарий продублирован')
          setDialog(null)
        },
        onError,
      })
    }
  }

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-xl font-bold text-ink">Сценарии продаж</h1>
          <p className="mt-0.5 text-[13px] text-ink-subtle">
            Активный сценарий движок аналитики применяет ко всем новым разговорам.
          </p>
        </div>
        <Link to="/scenarios/new">
          <Button variant="primary">
            <Plus className="size-4" />
            Создать сценарий
          </Button>
        </Link>
      </header>

      <Card>
        {scenarios.isError ? (
          <ErrorState
            message={apiErrorLabel(
              (scenarios.error as { detail?: unknown }).detail,
              'Не удалось загрузить сценарии',
            )}
            onRetry={() => void scenarios.refetch()}
          />
        ) : scenarios.isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} className="h-11" />
            ))}
          </div>
        ) : (scenarios.data?.length ?? 0) === 0 ? (
          <EmptyState
            title="Сценариев пока нет"
            description="Создайте первый сценарий — стартовый чек-лист шагов уже подготовлен."
            action={
              <Link to="/scenarios/new" className="mt-2">
                <Button variant="primary" size="sm">
                  Создать сценарий
                </Button>
              </Link>
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="border-b border-line text-left text-[13px] text-ink-subtle">
                  <th scope="col" className="px-5 py-2.5 font-medium">
                    Название
                  </th>
                  <th scope="col" className="px-5 py-2.5 font-medium">
                    Текущая версия
                  </th>
                  <th scope="col" className="px-5 py-2.5 font-medium">
                    Обновлён
                  </th>
                  <th scope="col" className="px-5 py-2.5 font-medium">
                    Статус
                  </th>
                  <th scope="col" className="px-5 py-2.5 text-right font-medium">
                    Действия
                  </th>
                </tr>
              </thead>
              <tbody>
                {scenarios.data?.map((scenario) => (
                  <tr
                    key={scenario.id}
                    className="border-b border-line/60 transition-colors last:border-0 hover:bg-surface-muted/60"
                  >
                    <td className="px-5 py-2.5">
                      <Link
                        to="/scenarios/$scenarioId"
                        params={{ scenarioId: scenario.id }}
                        className="cursor-pointer font-medium text-brand-700 transition-colors hover:text-brand-500 dark:text-brand-300"
                      >
                        {scenario.name}
                      </Link>
                    </td>
                    <td className="px-5 py-2.5 font-mono text-[13px] text-ink-muted">
                      {scenario.current_version ?? '—'}
                    </td>
                    <td className="px-5 py-2.5 text-[13px] text-ink-muted">
                      {formatDateTime(scenario.updated_at)}
                    </td>
                    <td className="px-5 py-2.5">
                      {scenario.is_active ? (
                        <Badge tone="positive">
                          <CheckCircle2 className="size-3.5" />
                          Активен
                        </Badge>
                      ) : (
                        <Badge tone="neutral">Неактивен</Badge>
                      )}
                    </td>
                    <td className="px-5 py-2.5">
                      <div className="flex items-center justify-end gap-1">
                        {!scenario.is_active && (
                          <Button size="sm" onClick={() => openDialog('activate', scenario)}>
                            Сделать активным
                          </Button>
                        )}
                        <Link to="/scenarios/$scenarioId" params={{ scenarioId: scenario.id }}>
                          <Button size="sm" variant="ghost" title="Редактировать">
                            <Pencil className="size-3.5" />
                          </Button>
                        </Link>
                        <Button
                          size="sm"
                          variant="ghost"
                          title="Переименовать"
                          onClick={() => openDialog('rename', scenario)}
                        >
                          <MoreHorizontal className="size-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          title="Дублировать"
                          onClick={() => openDialog('duplicate', scenario)}
                        >
                          <Copy className="size-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {(scenarios.data?.length ?? 0) > 0 && (
        <ScenarioBindingsSection scenarios={scenarios.data ?? []} />
      )}

      <Modal
        open={dialog?.kind === 'rename' || dialog?.kind === 'duplicate'}
        onOpenChange={(open) => !open && setDialog(null)}
        title={dialog?.kind === 'rename' ? 'Переименовать сценарий' : 'Дублировать сценарий'}
        description={
          dialog?.kind === 'duplicate'
            ? 'Копия создаётся неактивной и содержит только текущую версию оригинала.'
            : 'Переименование не создаёт новую версию сценария.'
        }
        footer={
          <>
            <Button onClick={() => setDialog(null)}>Отмена</Button>
            <Button
              variant="primary"
              loading={rename.isPending || duplicate.isPending}
              onClick={submitName}
            >
              {dialog?.kind === 'rename' ? 'Переименовать' : 'Дублировать'}
            </Button>
          </>
        }
      >
        <Field label="Название сценария" htmlFor="scenario-name">
          <Input
            id="scenario-name"
            value={nameValue}
            onChange={(event) => setNameValue(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && submitName()}
            autoComplete="off"
          />
        </Field>
      </Modal>

      <ConfirmDialog
        open={dialog?.kind === 'activate'}
        onOpenChange={(open) => !open && setDialog(null)}
        title={`Сделать «${dialog?.scenario.name ?? ''}» активным?`}
        description="Движок аналитики сразу начнёт применять этот сценарий к новым разговорам. Прежний активный станет неактивным — во всей системе активен ровно один."
        confirmLabel="Сделать активным"
        loading={activate.isPending}
        onConfirm={() => {
          if (!dialog) return
          activate.mutate(dialog.scenario.id, {
            onSuccess: () => {
              toast.success('Сценарий сделан активным')
              setDialog(null)
            },
            onError,
          })
        }}
      />
    </div>
  )
}
